import React from 'react';
import Routes from "./routes";
import { ReactKeycloakProvider } from "@react-keycloak/web";
import keycloak from "./keycloak";
import {BrandThemingProvider, DateLocalizationProvider, LoadingScreen, LoadingScreenProvider, SentryErrorBoundary} from '@dreebit-registry/vsm-microservice-components';
import {ApolloProvider} from "@apollo/client";
import {client} from "./graphql/client";
import 'react-toastify/dist/ReactToastify.css';
import AppToastContainers from "./components/SystemAnnouncements/AppToastContainers";
import {wasUserInitiatedLogout} from "./utils/localLogout";
import {silentReauth} from "./utils/silentReauth";
import KeycloakSessionHandler from "./keycloak/KeycloakSessionHandler";
import DynamicBrandTheming from "./provider/DynamicBrandTheming";
import {SnackbarProvider} from "./provider/snackbar";
import { LicenseInfo } from '@mui/x-license';
import * as Sentry from "@sentry/react";
import { installTokenEndpointMonitor, getLastTokenResponseForSentry } from "./keycloak/tokenEndpointMonitor";

installTokenEndpointMonitor();

if (process.env.REACT_APP_MUI_LICENSE_KEY) {
    LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_LICENSE_KEY);
}


function Root() {
  return (
    <SentryErrorBoundary>
      <div className="full-height">
          <BrandThemingProvider>
              <LoadingScreenProvider>
                      <ReactKeycloakProvider
                          authClient={keycloak}
                          initOptions={{
                              onLoad: 'login-required',
                              scope: 'otto_installed_base otto_crm otto_iot',
                              checkLoginIframe: false,
                          }}
                          LoadingComponent={<LoadingScreen/>}
                          isLoadingCheck={(keycloak) => !keycloak.authenticated}
                          onEvent={(event, error) => {
                              if (event === 'onAuthRefreshError') {
                                  const tokenResponse = getLastTokenResponseForSentry();
                                  const serverError = tokenResponse.lastTokenResponse?.error;
                                  const serverErrorDescription =
                                      tokenResponse.lastTokenResponse?.errorDescription;
                                  console.error(
                                      `[Keycloak] Token refresh failed: ${serverError ?? 'unknown'}` +
                                          (serverErrorDescription ? ` — ${serverErrorDescription}` : ''),
                                      { tokenEndpointStatus: tokenResponse.lastTokenResponse?.status, error },
                                  );
                                  Sentry.captureException(
                                      error instanceof Error
                                          ? error
                                          : new Error('Keycloak onAuthRefreshError'),
                                      {
                                          level: 'warning',
                                          tags: {
                                              keycloak: 'onAuthRefreshError',
                                              tokenEndpointStatus: String(
                                                  tokenResponse.lastTokenResponse?.status ?? 'unknown',
                                              ),
                                              tokenEndpointError: String(serverError ?? 'unknown'),
                                          },
                                          extra: {
                                              userId: keycloak.subject,
                                              authenticated: keycloak.authenticated,
                                              tabVisibility: document.visibilityState,
                                              errorValue: error,
                                              ...tokenResponse,
                                          },
                                      },
                                  );
                              }
                              if (event === 'onAuthLogout') {
                                  if (wasUserInitiatedLogout()) {
                                      Sentry.addBreadcrumb({
                                          category: 'keycloak',
                                          level: 'info',
                                          message: 'onAuthLogout fired during user-initiated logout',
                                      });
                                      return;
                                  }
                                  Sentry.captureMessage(
                                      'Keycloak onAuthLogout fired (silent re-auth)',
                                      {
                                          level: 'warning',
                                          tags: { keycloak: 'onAuthLogout' },
                                          extra: {
                                              userId: keycloak.subject,
                                              tabVisibility: document.visibilityState,
                                          },
                                      },
                                  );
                                  silentReauth(keycloak, 'onAuthLogout');
                              }
                              if (event === 'onTokenExpired') {
                                  Sentry.addBreadcrumb({
                                      category: 'keycloak',
                                      level: 'info',
                                      message: 'onTokenExpired event fired',
                                      data: {
                                          userId: keycloak.subject,
                                          tabVisibility: document.visibilityState,
                                      },
                                  });
                              }
                          }}
                      >
                      <DynamicBrandTheming>
                          <ApolloProvider client={client}>
                              <DateLocalizationProvider>
                                  <SnackbarProvider>
                                      <AppToastContainers/>
                                      <KeycloakSessionHandler />
                                      <Routes />
                                  </SnackbarProvider>
                              </DateLocalizationProvider>
                          </ApolloProvider>
                      </DynamicBrandTheming>
                  </ReactKeycloakProvider>
              </LoadingScreenProvider>
          </BrandThemingProvider>
      </div>
    </SentryErrorBoundary>
  );
}

export default Root;
